import router from "@/router";
import { createStore } from "vuex";
import {
  dispatchEvent,
  getAccountFromId,
  getAccountStripePortal,
} from "./service";
import {
  getClaimsClient,
  getClaimsVehicleLookup,
  createThirdPartyClaim,
  createThirdPartyClaimAttachment,
  submitThirdPartyClaim,
  getClaimLookup,
} from "./claims-service";
import { useAuth } from "./auth";

export default createStore({
  state() {
    return {
      launchDarkly: null,
      user: null,
      loading: false,
      account: null,
    };
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setItem(state, { key, val }) {
      state[key] = val;
    },
    setClaimFormItems(state, items) {
      state.claimForm = state.claimForm ?? {};
      Object.keys(items).forEach((key) => {
        state.claimForm[key] = items[key];
      });
    },
    setLookupClaim(state, lookupClaim) {
      state.claimLookup = lookupClaim;
    },
  },
  actions: {
    async initialLoad({ dispatch, commit }) {
      try {
        commit("setLoading", true);

        // Get the current authenticated user, if any
        const user = await dispatch("getCurrentUser");

        // Determine if the current page allows unauthenticated access
        const anonymousRoutes = router
          .getRoutes()
          .filter((rt) => rt.meta?.allowAnonymous)
          .map((rt) => rt.path);
        const currentPathName = window.location.pathname;
        const allowAnonymous = anonymousRoutes.includes(
          currentPathName.replace(/\/$/, ""),
        );

        // If the user is not authenticated and the page requires authentication, send the user
        // to the login page.
        if (user == null && !allowAnonymous) {
          const auth = await useAuth();
          await auth.login();
          return;
        }

        // Try to get an account associated with this authenticated user
        if (user != null) {
          const account = await dispatch("getAccount");
          commit("setItem", { key: "account", val: account });
        }
      } catch (ex) {
        console.log(ex);
      } finally {
        commit("setLoading", false);
      }
    },
    async getCurrentUser({ commit }) {
      const auth = await useAuth();
      const user = await auth.getUser();
      commit("setUser", user);
      return user;
    },
    async signOut() {
      const auth = await useAuth();
      await auth.logout();
    },
    async getAccount({ commit }) {
      const account = await getAccountFromId();
      commit("setItem", { key: "account", val: account });
      return account;
    },
    async getAccountStripePortal(context, { returnUrl }) {
      return await getAccountStripePortal(returnUrl);
    },
    async initClaimsClient({ commit }) {
      const claimsClient = await getClaimsClient();
      commit("setItem", { key: "claimsClient", val: claimsClient });
    },
    createThirdPartyClaim({ state }) {
      return createThirdPartyClaim(state.claimsClient, {
        ...state.claimForm,
        started: state.claimStartDate,
      });
    },
    submitThirdPartyClaim({ state }, { claimId, tenantId }) {
      return submitThirdPartyClaim(state.claimsClient, claimId, tenantId);
    },
    createThirdPartyClaimAttachment({ state }, { file, claimId, accountId }) {
      return createThirdPartyClaimAttachment(
        state.claimsClient,
        claimId,
        accountId,
        file,
      );
    },
    getClaimsVehicleLookup({ state }, { vin, incidentDate }) {
      return getClaimsVehicleLookup(state.claimsClient, vin, incidentDate);
    },
    getClaimLookup({ state }, { claimNumber, incidentDate, vehicleId }) {
      return getClaimLookup(
        state.claimsClient,
        claimNumber,
        incidentDate,
        vehicleId,
      );
    },
    async getLaunchDarklyFlag({ state }, flag) {
      await state.launchDarkly.waitForInitialization();
      return state.launchDarkly?.variation(flag, false);
    },
    logEvent({ state }, { name, data }) {
      dispatchEvent(name, {
        application: "lula_app",
        uid: state.user?.email,
        accountId: state.account?.id,
        email: state.user?.email,
        ...(data || {}),
      });
    },
  },
});
